import React, { useEffect, useState, useRef } from 'react';
import { BellIcon, LogOutIcon, CheckCircleIcon, AlertTriangleIcon, InfoIcon, XCircleIcon, MenuIcon } from 'lucide-react';
import { User, Notification } from '../types';
interface TopNavProps {
  pageTitle: string;
  user: User;
  notificationCount: number;
  notifications?: Notification[];
  onLogout: () => void;
  onToggleSidebar?: () => void;
}
export function TopNav({
  pageTitle,
  user,
  notificationCount,
  notifications = [],
  onLogout,
  onToggleSidebar
}: TopNavProps) {
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const initials = user.name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setIsNotifOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const getNotifIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="w-4 h-4 text-[#00FF88]" data-id="element-101" />;
      case 'warning':
        return <AlertTriangleIcon className="w-4 h-4 text-[#FFB800]" data-id="element-102" />;
      case 'error':
        return <XCircleIcon className="w-4 h-4 text-[#FF3B3B]" data-id="element-103" />;
      default:
        return <InfoIcon className="w-4 h-4 text-[#00D4FF]" data-id="element-104" />;
    }
  };
  const getNotifColor = (type: string) => {
    switch (type) {
      case 'success':
        return 'bg-[#00FF88]';
      case 'warning':
        return 'bg-[#FFB800]';
      case 'error':
        return 'bg-[#FF3B3B]';
      default:
        return 'bg-[#00D4FF]';
    }
  };
  return <header className="h-16 bg-[#0B0F1A] border-b border-[#1E3A5F] flex items-center justify-between px-4 sm:px-6 sticky top-0 z-30 relative overflow-visible" data-id="element-105">
      <div className="absolute inset-0 scanline opacity-10 pointer-events-none" data-id="element-106"></div>

      <div className="flex items-center relative z-10" data-id="element-107">
        {onToggleSidebar && <button onClick={onToggleSidebar} className="lg:hidden mr-4 text-[#94A3B8] hover:text-[#00D4FF] transition-colors" data-id="element-108">
            <MenuIcon className="w-6 h-6" data-id="element-109" />
          </button>}
        <div className="hidden sm:block w-2 h-2 rounded-full bg-[#00D4FF] mr-3 glow-cyan-strong" data-id="element-110"></div>
        <h2 className="text-lg sm:text-xl font-bold text-[#E2E8F0] tracking-wide uppercase truncate max-w-[200px] sm:max-w-md" data-id="element-111">
          {pageTitle}
        </h2>
      </div>

      <div className="flex items-center space-x-3 sm:space-x-6 relative z-10" data-id="element-112">
        {/* Notifications */}
        <div className="relative" ref={notifRef} data-id="element-113">
          <button onClick={() => setIsNotifOpen(!isNotifOpen)} className={`relative p-2 transition-colors rounded ${isNotifOpen ? 'text-[#00D4FF] bg-[#111827]' : 'text-[#94A3B8] hover:text-[#00D4FF] hover:bg-[#111827]'}`} data-id="element-114">
            <BellIcon className="w-5 h-5" data-id="element-115" />
            {notificationCount > 0 && <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-[#FF3B3B] rounded-full ring-2 ring-[#0B0F1A] glow-red" data-id="element-116"></span>}
          </button>

          {/* Notification Dropdown */}
          {isNotifOpen && <div className="absolute right-0 mt-2 w-80 bg-[#111827] border border-[#1E3A5F] rounded shadow-2xl overflow-hidden z-50" data-id="element-117">
              <div className="p-3 border-b border-[#1E3A5F] bg-[#0F1629] flex justify-between items-center" data-id="element-118">
                <h3 className="text-sm font-bold text-[#E2E8F0] uppercase tracking-wide" data-id="element-119">
                  Notifikasi
                </h3>
                <span className="bg-[#00D4FF]/10 text-[#00D4FF] text-xs font-bold px-2 py-0.5 rounded" data-id="element-120">
                  {notificationCount} Baru
                </span>
              </div>

              <div className="max-h-80 overflow-y-auto" data-id="element-121">
                {notifications.length > 0 ? notifications.map(notif => <div key={notif.id} className={`p-3 border-b border-[#1E3A5F]/50 hover:bg-[#1A2332] transition-colors flex items-start gap-3 cursor-pointer ${!notif.isRead ? 'bg-[#00D4FF]/5' : ''}`} data-id="element-122">
                      <div className="mt-0.5" data-id="element-123">{getNotifIcon(notif.type)}</div>
                      <div className="flex-1" data-id="element-124">
                        <p className={`text-sm ${!notif.isRead ? 'font-bold text-[#E2E8F0]' : 'font-medium text-[#E2E8F0]'}`} data-id="element-125">
                          {notif.title}
                        </p>
                        <p className="text-xs text-[#94A3B8] mt-1 line-clamp-2" data-id="element-126">
                          {notif.message}
                        </p>
                        <p className="text-[10px] text-[#64748B] font-mono mt-1.5" data-id="element-127">
                          {new Date(notif.createdAt).toLocaleString('id-ID')}
                        </p>
                      </div>
                      {!notif.isRead && <div className={`w-2 h-2 rounded-full mt-1.5 ${getNotifColor(notif.type)}`} data-id="element-128"></div>}
                    </div>) : <div className="p-6 text-center text-[#94A3B8] text-sm" data-id="element-129">
                    Tidak ada notifikasi.
                  </div>}
              </div>

              {notifications.length > 0 && <div className="p-2 border-t border-[#1E3A5F] bg-[#0B0F1A]" data-id="element-130">
                  <button className="w-full text-center text-xs font-bold text-[#00D4FF] hover:text-[#00D4FF]/80 py-1 uppercase tracking-wider" data-id="element-131">
                    Tandai Semua Dibaca
                  </button>
                </div>}
            </div>}
        </div>

        <div className="h-6 w-px bg-[#1E3A5F] hidden sm:block" data-id="element-132"></div>

        {/* User Profile */}
        <div className="flex items-center space-x-3" data-id="element-133">
          <div className="hidden sm:flex flex-col items-end" data-id="element-134">
            <span className="text-sm font-semibold text-[#E2E8F0]" data-id="element-135">
              {user.name}
            </span>
            <span className="text-xs text-[#00D4FF]" data-id="element-136">{user.role}</span>
          </div>
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded bg-[#00D4FF]/20 text-[#00D4FF] border border-[#00D4FF]/30 flex items-center justify-center font-bold text-xs sm:text-sm shadow-sm glow-cyan shrink-0" data-id="element-137">
            {initials}
          </div>
        </div>

        {/* Logout */}
        <button onClick={onLogout} className="p-2 text-[#94A3B8] hover:text-[#FF3B3B] transition-colors rounded hover:bg-[#FF3B3B]/10" title="Keluar" data-id="element-138">
          <LogOutIcon className="w-5 h-5" data-id="element-139" />
        </button>
      </div>
    </header>;
}